// The Dream entry boosts, read from the Dream summary the way the embed
// (dreamBoost.js, dreamStreak.js, dreamWin.js) and the app read it.

function toMs(value) {
  const ms = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
}

export function canShowBoosts(summary) {
  return !!(summary && summary.enabled && summary.period);
}

function activeBoosts(summary) {
  return Array.isArray(summary?.boosts?.active) ? summary.boosts.active : [];
}

/** The school-wide (or network-wide) boost running now, for the day banner and the chips. */
export function networkBannerBoost(summary, nowMs) {
  if (!canShowBoosts(summary)) return null;
  const now = nowMs != null ? nowMs : Date.now();
  for (const boost of activeBoosts(summary)) {
    if (!boost || (boost.scope !== 'global' && boost.scope !== 'school')) continue;
    const endsAt = toMs(boost.ends_at);
    const multiplier = Math.trunc(Number(boost.multiplier));
    if (!endsAt || endsAt <= now || !Number.isFinite(multiplier) || multiplier <= 1) continue;
    return {
      multiplier,
      endsAt,
      title: String(boost.title || '').trim(),
      showImage: boost.show_image === true,
    };
  }
  return null;
}

export function boostBannerTitle(boost) {
  const custom = String(boost?.title || '').trim();
  return custom || `${boost.multiplier}X entries today`;
}

/** When the last running boost ends; 0 with none, or at the entry cap where purchases earn nothing more. */
export function boostCalloutEndsAt(summary, nowMs) {
  if (!canShowBoosts(summary) || summary.at_entry_cap === true) return 0;
  const now = nowMs != null ? nowMs : Date.now();
  let max = 0;
  activeBoosts(summary).forEach((boost) => {
    const endsAt = toMs(boost?.ends_at);
    if (endsAt && endsAt > now && endsAt > max) max = endsAt;
  });
  return max;
}

/** A merchant's or offer's own entry multiplier (2, 5, 8, 10); null below 2. */
export function getEntryMultiplierValue(item) {
  if (!item || typeof item !== 'object') return null;
  const raw = item.entryMultiplier != null ? item.entryMultiplier : item.entry_multiplier;
  const multiplier = Math.trunc(Number(raw));
  if (!Number.isFinite(multiplier) || multiplier <= 1) return null;
  return multiplier;
}

// --- This week strip -------------------------------------------------------

const DENVER = 'America/Denver';
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function denverParts(ms, withTime) {
  const options = withTime
    ? { timeZone: DENVER, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }
    : { timeZone: DENVER, year: 'numeric', month: '2-digit', day: '2-digit' };
  const value = {};
  new Intl.DateTimeFormat('en-US', options).formatToParts(new Date(ms)).forEach((part) => {
    value[part.type] = part.value;
  });
  return value;
}

function denverOffset(ms) {
  const v = denverParts(ms, true);
  const asUtc = Date.UTC(Number(v.year), Number(v.month) - 1, Number(v.day), Number(v.hour) % 24, Number(v.minute), Number(v.second));
  return asUtc - ms;
}

function denverMidnight(year, month, day) {
  const guess = Date.UTC(year, month - 1, day);
  return guess - denverOffset(guess - denverOffset(guess));
}

function networkBoosts(summary) {
  const boosts = summary?.boosts || {};
  return []
    .concat(boosts.week || [], boosts.active || [], boosts.upcoming || [])
    .filter((boost) => boost && (boost.scope === 'global' || boost.scope === 'school'));
}

/** Monday to Sunday of this week (Denver days) with the best multiplier of each day. */
export function dreamWeek(summary, nowMs) {
  if (!canShowBoosts(summary)) return null;
  const now = nowMs != null ? nowMs : Date.now();
  const today = denverParts(now, false);
  const todayUtc = Date.UTC(Number(today.year), Number(today.month) - 1, Number(today.day));
  const sinceMonday = (new Date(todayUtc).getUTCDay() + 6) % 7;
  const boosts = networkBoosts(summary);
  const days = [];
  for (let index = 0; index < 7; index += 1) {
    const utc = todayUtc + (index - sinceMonday) * 86400000;
    const date = new Date(utc);
    const next = new Date(utc + 86400000);
    const start = denverMidnight(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
    const end = denverMidnight(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate());
    let multiplier = 0;
    boosts.forEach((boost) => {
      const starts = Date.parse(boost.starts_at);
      const ends = Date.parse(boost.ends_at);
      const value = Math.trunc(Number(boost.multiplier));
      if (!Number.isFinite(starts) || !Number.isFinite(ends) || !Number.isFinite(value) || value <= 1) return;
      if (starts < end && ends > start && value > multiplier) multiplier = value;
    });
    days.push({ label: DAY_LABELS[date.getUTCDay()], multiplier: multiplier || null, today: index === sinceMonday });
  }
  const count = days.filter((day) => day.multiplier).length;
  const peak = days.reduce((max, day) => Math.max(max, day.multiplier || 0), 0);
  return { days, count, peak };
}

// --- Purchase streak -------------------------------------------------------

function bonusEntries(streak) {
  const bonus = Number(streak?.next_milestone?.bonus_entries);
  if (!Number.isFinite(bonus) || bonus <= 0) return 0;
  return Math.trunc(bonus);
}

/** "3 more days to +5 entries"; "" when there is no streak worth showing. */
export function streakWidgetCopy(streak) {
  if (!streak || typeof streak !== 'object') return '';
  const current = Number(streak.current_days);
  if (!Number.isFinite(current) || current <= 0) return '';
  const bonus = bonusEntries(streak);
  if (!bonus) return '';
  const daysToNext = Number(streak.days_to_next);
  if (!Number.isFinite(daysToNext) || daysToNext <= 0) return '';
  if (daysToNext === 1) {
    const when = streak.today_counts === true ? 'tomorrow' : 'today';
    return `${Math.trunc(current)}-day streak — one qualifying purchase ${when} unlocks +${bonus} entries`;
  }
  return `${Math.trunc(daysToNext)} more days to +${bonus} entries`;
}

/** The streak line Home and Profile show; "" when the program is off or the fan is at the cap. */
export function streakLine(summary) {
  if (!canShowBoosts(summary) || summary.at_entry_cap === true) return '';
  return streakWidgetCopy(summary.streak);
}

// --- Dream win -------------------------------------------------------------

const WIN_SEEN_PREFIX = 'hc_dream_win_seen_';

function winSeen(prizeId) {
  try {
    return localStorage.getItem(WIN_SEEN_PREFIX + String(prizeId)) === '1';
  } catch (_e) {
    return false;
  }
}

export function markDreamWinSeen(prizeId) {
  try {
    localStorage.setItem(WIN_SEEN_PREFIX + String(prizeId), '1');
  } catch (_e) {}
}

/** The fan's own win they have not been shown yet, if any. */
export function unseenDreamWin(winners) {
  const list = Array.isArray(winners) ? winners : [];
  return list.find((win) => win && win.is_you && win.prize_id && !winSeen(win.prize_id)) || null;
}
